import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Users, CalendarDays, Plus, Trash2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";

export default function AdminAdvisorDetail() {
  const { advisorId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/advisors/${advisorId}`);
      setDetail(data);
      const adv = data.advisor;
      setForm({
        name: adv.name || "",
        phone: adv.phone || "",
        bio: adv.bio || "",
        experience_yrs: adv.experience_yrs ?? 0,
        languages: (adv.languages || []).join(", "),
        specialization: (adv.specialization || []).join(", "),
      });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [advisorId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/advisors/${advisorId}`, {
        ...form,
        experience_yrs: Number(form.experience_yrs) || 0,
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
        specialization: form.specialization.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Advisor updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const assignClient = async () => {
    if (!assignUserId.trim()) return;
    try {
      await api.post(`/advisors/${advisorId}/clients`, { user_id: assignUserId.trim() });
      toast.success("Client assigned");
      setAssignUserId("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeClient = async (userId) => {
    try {
      await api.delete(`/advisors/${advisorId}/clients/${userId}`);
      toast.success("Client removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return (
    <AdminGuard>
      <AdminLayout>
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>
      </AdminLayout>
    </AdminGuard>
  );

  const adv = detail?.advisor;

  return (
    <AdminGuard>
      <AdminLayout>
        <button onClick={() => navigate("/admin/advisors")} className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] mb-6 font-heading uppercase tracking-wider transition-colors">
          <ArrowLeft size={15} /> Back
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--zt-teal))] grid place-items-center text-white text-xl font-bold">
                  {adv?.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="font-display text-lg text-[hsl(var(--zt-teal))] font-bold">{adv?.name}</h2>
                  <StatusBadge status={adv?.is_active ? "active" : "inactive"} />
                </div>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <p className="text-[hsl(var(--zt-charcoal))]/60">{adv?.email}</p>
                <p className="text-[hsl(var(--zt-charcoal))]/60">{adv?.phone || "—"}</p>
                <p className="text-[hsl(var(--zt-charcoal))]/60">{adv?.experience_yrs} years experience</p>
              </div>

              {adv?.bio && (
                <p className="text-sm text-[hsl(var(--zt-charcoal))]/65 leading-relaxed border-t border-[hsl(var(--zt-beige-deep))] pt-4">
                  {adv.bio}
                </p>
              )}

              {(adv?.languages?.length > 0 || adv?.specialization?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--zt-beige-deep))] space-y-3">
                  {adv.languages?.length > 0 && (
                    <div>
                      <p className="text-[9px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 mb-1.5">Languages</p>
                      <div className="flex flex-wrap gap-1">
                        {adv.languages.map(l => <span key={l} className="text-[9px] font-heading uppercase tracking-wider bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-charcoal))]/60 px-2 py-0.5 rounded-full">{l}</span>)}
                      </div>
                    </div>
                  )}
                  {adv.specialization?.length > 0 && (
                    <div>
                      <p className="text-[9px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 mb-1.5">Specialization</p>
                      <div className="flex flex-wrap gap-1">
                        {adv.specialization.map(s => <span key={s} className="text-[9px] font-heading uppercase tracking-wider bg-[hsl(var(--zt-teal))]/8 text-[hsl(var(--zt-teal))]/70 px-2 py-0.5 rounded-full">{s}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm grid grid-cols-2 gap-3">
              {[
                { label: "Clients", value: detail?.clients?.length ?? 0 },
                { label: "Today's Apts", value: detail?.todays_appointments?.length ?? 0 },
                { label: "Upcoming", value: detail?.upcoming_appointments?.length ?? 0 },
                { label: "Completed", value: detail?.stats?.completed ?? 0 },
              ].map(s => (
                <div key={s.label} className="text-center py-2">
                  <p className="font-display text-2xl text-[hsl(var(--zt-teal))] font-bold">{s.value}</p>
                  <p className="text-[9px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/45 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Edit button */}
            {editing ? (
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm space-y-3">
                {[
                  { key: "name", label: "Name", type: "text" },
                  { key: "phone", label: "Phone", type: "text" },
                  { key: "experience_yrs", label: "Years Exp", type: "number" },
                  { key: "languages", label: "Languages (csv)", type: "text" },
                  { key: "specialization", label: "Specialization (csv)", type: "text" },
                ].map(f => (
                  <div key={f.key}>
                    <label htmlFor={`adv-${f.key}`} className="block text-[9px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 mb-1">{f.label}</label>
                    <input id={`adv-${f.key}`} type={f.type} value={form[f.key] ?? ""} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full h-8 px-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30" />
                  </div>
                ))}
                <div>
                  <label htmlFor="adv-bio" className="block text-[9px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 mb-1">Bio</label>
                  <textarea id="adv-bio" value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                    className="w-full h-16 px-2 py-1.5 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none resize-none" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={save} disabled={saving} size="sm" className="flex-1 bg-[hsl(var(--zt-teal))] text-white rounded-lg h-8 text-xs font-heading uppercase tracking-wider">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <><Save size={12} className="mr-1" />Save</>}
                  </Button>
                  <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="rounded-lg h-8 text-xs font-heading uppercase">Cancel</Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setEditing(true)} className="w-full bg-[hsl(var(--zt-teal))] text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                Edit Advisor
              </Button>
            )}
          </div>

          {/* Clients + Appointments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Assign client */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm">
              <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-3">
                <Users size={13} className="inline mr-2" />Assign Client
              </h3>
              <div className="flex gap-2">
                <input
                  type="text" placeholder="User ID…" value={assignUserId}
                  onChange={e => setAssignUserId(e.target.value)}
                  className="flex-1 h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30"
                />
                <Button onClick={assignClient} disabled={!assignUserId.trim()} size="sm"
                  className="bg-[hsl(var(--zt-teal))] text-white rounded-xl h-9 px-4 font-heading uppercase tracking-wider text-xs">
                  <Plus size={13} className="mr-1" /> Assign
                </Button>
              </div>
            </div>

            {/* Client list */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
                  Clients ({detail?.clients?.length ?? 0})
                </h3>
              </div>
              {detail?.clients?.length ? (
                <ul className="divide-y divide-[hsl(var(--zt-beige-deep))]/60">
                  {detail.clients.map(c => (
                    <li key={c.user_id} className="px-6 py-3.5 flex items-center justify-between hover:bg-[hsl(var(--zt-beige))]/30">
                      <div>
                        <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{c.name || "—"}</p>
                        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{c.email} · {c.country || "—"}</p>
                      </div>
                      <button onClick={() => removeClient(c.user_id)} className="w-7 h-7 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 grid place-items-center transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-6 py-8 text-sm text-center text-[hsl(var(--zt-charcoal))]/40">No clients assigned</p>
              )}
            </div>

            {/* Today's appointments */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
                  <CalendarDays size={13} className="inline mr-2" />Today's Appointments
                </h3>
              </div>
              {detail?.todays_appointments?.length ? (
                <ul className="divide-y divide-[hsl(var(--zt-beige-deep))]/60">
                  {detail.todays_appointments.map(a => (
                    <li key={a.appointment_id} className="px-6 py-3.5 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{a.user_name || a.user_email}</p>
                        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtTime(a.scheduled_at)} · {a.meeting_mode}</p>
                      </div>
                      <StatusBadge status={a.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-6 py-8 text-sm text-center text-[hsl(var(--zt-charcoal))]/40">No appointments today</p>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
